import React, { FocusEvent, useCallback, useState } from 'react';
import { TextInput } from '../../components/TextInput';
import { identifyComponent } from '../../util';

/**
 * Checks the string value is convertable to a number and
 * meets possible positive or integer constraints.
 */
const validNumber = (value: string, positive: boolean, integer: boolean) => {
  // if conversion fails it's not a valid number
  if (!Number.isFinite(Number(value))) return false;

  // no negations when positive
  if (positive && (value.match(/-/g) || []).length) {
    return false;
  }

  // no decimals when integer
  if (integer && (value.match(/\./g) || []).length) {
    return false;
  }

  return true;
};

export interface NumberTextInputProps
  extends Omit<React.ComponentProps<typeof TextInput>, 'css' | 'type' | 'onChange'> {
  /**
   * Have to provide a value and make it a controlled component.
   */
  value: number;
  /**
   * The parsed number, 0 if empty.
   */
  onChange: (value: number) => void;
  /**
   * Restricts input to only allow positive numbers
   */
  positive?: boolean;
  /**
   * Restricts input to only allow integers
   */
  integer?: boolean;
}

/**
 * Wrapper over the TextInput component to better handle text inputs dealing with numbers.
 * Displays a pure number without commas when focused, and a locale formatted one when not.
 * Handles special cases like zero vs an empty string.
 * Constrains input to specified numbers only.
 */
export const NumberTextInput = React.forwardRef<HTMLInputElement, NumberTextInputProps>(
  ({ value, onChange, onFocus, onBlur, positive = false, integer = false, ...rest }, ref) => {
    const [focused, setFocused] = useState(false);
    // there are special cases where the input should read differently than the converted number.
    // this supports a natural entry flow while internalizing the logic to convert the input to a number on change.
    // examples include discriminating 0 vs empty string, a leading negation, trailing decimals 10.0...
    const [tempValue, setTempValue] = useState<null | string>(null);

    const handleChange = useCallback(
      (value: string) => {
        // special cases, leading negation symbol for a negative number or leading decimal,
        // we will consider this still be 0
        if ((!positive && value === '-') || value === '.') {
          setTempValue(value);
          onChange(0);
          return;
        }

        // strip commas and leading/trailing whitespace
        const result = value.trim().replace(/,/g, '');

        if (!validNumber(result, positive, integer)) return;

        // special cases to show a temporary value
        if (!integer && result.replace(/0/g, '').endsWith('.')) {
          // supports trailing unfinished decimals for integers
          // this could be '10.' or '10.0' or '10.00'...
          setTempValue(result);
        } else {
          // support an empty string to discriminate an empty value vs 0
          setTempValue(result === '' ? '' : null);
        }

        // in the special cases above, this will convert like:
        // '' => 0
        // '10.' or '10.00' => 10
        onChange(Number(result));
      },
      [onChange, positive, integer],
    );

    const handleFocus = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        if (onFocus) onFocus(e);
      },
      [onFocus],
    );
    const handleBlur = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        // clear out any temp value on blur to reset
        setTempValue(null);
        if (onBlur) onBlur(e);
      },
      [onBlur],
    );

    return (
      <TextInput
        data-component="NumberTextInput"
        {...rest}
        {...identifyComponent('number-text-input', rest)}
        type="text"
        value={focused ? (tempValue !== null ? tempValue : value) : value.toLocaleString()}
        onChange={e => handleChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        ref={ref}
      />
    );
  },
);

NumberTextInput.displayName = 'NumberTextInput';
